"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Info } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

interface AWSSignatureFormProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  connectionConfig: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
}

const AWS_REGIONS = [
  "us-east-1", "us-east-2", "us-west-1", "us-west-2",
  "eu-west-1", "eu-west-2", "eu-west-3", "eu-central-1", "eu-north-1",
  "ap-southeast-1", "ap-southeast-2", "ap-northeast-1", "ap-northeast-2", "ap-south-1",
  "sa-east-1", "ca-central-1",
];

const AWS_SERVICES = [
  { value: "s3", label: "S3 (Storage)" },
  { value: "dynamodb", label: "DynamoDB" },
  { value: "lambda", label: "Lambda" },
  { value: "execute-api", label: "API Gateway" },
  { value: "secretsmanager", label: "Secrets Manager" },
  { value: "sqs", label: "SQS" },
  { value: "sns", label: "SNS" },
  { value: "ec2", label: "EC2" },
  { value: "iam", label: "IAM" },
  { value: "sts", label: "STS" },
];

export function AWSSignatureForm({
  config,
  onChange,
  connectionConfig,
  onConfigChange,
}: AWSSignatureFormProps) {
  const [showSecret, setShowSecret] = useState(false);
  const [showSession, setShowSession] = useState(false);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 text-amber-400" />
          <div className="text-xs text-amber-200">
            <p className="font-medium">AWS Signature Version 4</p>
            <p className="mt-1 text-amber-200/70">
              Requests will be signed using AWS SigV4. Never expose your secret access key.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-white/80">Access Key ID</Label>
        <Input
          value={(config.access_key_id as string) || ""}
          onChange={(e) => onChange({ access_key_id: e.target.value })}
          placeholder="AKIAIOSFODNN7EXAMPLE"
          className="border-white/10 bg-white/5 font-mono text-white placeholder:text-white/40"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-white/80">Secret Access Key</Label>
        <div className="relative">
          <Input
            type={showSecret ? "text" : "password"}
            value={(config.secret_access_key as string) || ""}
            onChange={(e) => onChange({ secret_access_key: e.target.value })}
            placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
            className="border-white/10 bg-white/5 pr-10 font-mono text-white placeholder:text-white/40"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-white/40 hover:text-white"
            onClick={() => setShowSecret(!showSecret)}
          >
            {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-white/80">Session Token (Optional)</Label>
        <div className="relative">
          <Input
            type={showSession ? "text" : "password"}
            value={(config.session_token as string) || ""}
            onChange={(e) => onChange({ session_token: e.target.value })}
            placeholder="For temporary credentials from STS"
            className="border-white/10 bg-white/5 pr-10 text-white placeholder:text-white/40"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-white/40 hover:text-white"
            onClick={() => setShowSession(!showSession)}
          >
            {showSession ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-white/40">
          Only needed for temporary credentials (e.g., from AssumeRole)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white/80">Region</Label>
          <select
            value={(connectionConfig.region as string) || "us-east-1"}
            onChange={(e) => onConfigChange({ region: e.target.value })}
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
          >
            {AWS_REGIONS.map((region) => (
              <option key={region} value={region} className="bg-slate-900">
                {region}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label className="text-white/80">Service</Label>
          <select
            value={(connectionConfig.service as string) || "s3"}
            onChange={(e) => onConfigChange({ service: e.target.value })}
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
          >
            {AWS_SERVICES.map((service) => (
              <option key={service.value} value={service.value} className="bg-slate-900">
                {service.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
